import { getPathSegment } from '../const/utils/getPathSegment';
import { ModuleTagging, NodeModel, StrictNodeModel } from '../types';

// Branch is grouped as a single node once more than this many children match; otherwise it is recursed into.
const MIN_MATCHING_CHILDREN_TO_GROUP = 3;

type FilterableNode = StrictNodeModel & {
  filterTagPath?: string;
  filterRoot?: NodeModel;
};

/**
 * Resolves a filterTagPath by filtering descendants from filterRoot
 * based on modules that match the tag path prefix.
 *
 * Supports ungrouping branches when children have different DisplayOrder values,
 * allowing the final sort to reorder modules according to metadata preferences.
 *
 * @param node - The node with filterTagPath and filterRoot
 * @param moduleTagging - Array of module taggings to filter against
 * @returns Array of filtered children as StrictNodeModel, sorted by DisplayOrder
 */
export function resolveFilterPath(
  node: FilterableNode,
  moduleTagging: readonly ModuleTagging[],
): readonly StrictNodeModel[] {
  const { filterTagPath, filterRoot } = node;
  if (!filterTagPath || !filterRoot) {
    return node.children;
  }

  // Build map of module keys to their DisplayOrder values
  const displayOrderMap = new Map<string, number>();
  const matchingTagging = moduleTagging.filter((t) =>
    t.tag.path.startsWith(filterTagPath),
  );

  matchingTagging.forEach((t) => {
    const key = getModuleKey(t.module.slug, t.module.variant);
    if (t.metaData?.DisplayOrder !== undefined) {
      displayOrderMap.set(key, t.metaData.DisplayOrder);
    }
  });

  // Build set of module slugs that match this tag path
  const matchingModules = new Set(
    matchingTagging.map((t) => getModuleKey(t.module.slug, t.module.variant)),
  );

  // Filter descendants from filterRoot (cast to StrictNodeModel since
  // standardizeNode has processed it before this function is called)
  const filtered = filterDescendants(
    filterRoot as StrictNodeModel,
    matchingModules,
    displayOrderMap,
    {
      path: node.key,
      parentColor: node.color,
    },
  ).map((child) => ({
    ...child,
    icon: normalizeFilteredIcon(child),
    label: child.filtered?.label ?? child.label,
    color: child.filtered?.color ?? child.color,
    duplicate: true,
  }));

  function sortOrder(node: StrictNodeModel): number {
    const key = node.moduleSlug
      ? getModuleKey(node.moduleSlug, node.moduleVariant)
      : null;
    return (
      (key && displayOrderMap.has(key)
        ? displayOrderMap.get(key)
        : undefined) ?? Number.MAX_SAFE_INTEGER
    );
  }

  // Sort by DisplayOrder, with modules lacking DisplayOrder at the end
  // For ties, preserve original tree order (stable sort)
  return filtered.sort((a, b) => {
    return sortOrder(a) - sortOrder(b);
  });
}

function filterDescendants(
  root: StrictNodeModel,
  matchingModules: Set<string>,
  displayOrderMap: Map<string, number>,
  ctx: { path: string; parentColor: number },
): StrictNodeModel[] {
  const results: StrictNodeModel[] = [];

  for (const [i, child] of root.children.entries()) {
    const pathSegment = getPathSegment(child, i);
    const childKey = `${ctx.path}/${pathSegment}`;

    if (child.moduleSlug) {
      // Leaf node with module - check if it matches.
      // Note: gym op nodes have moduleSlug AND children (gym number nodes with
      // no tag/moduleSlug). Their children's keys must be remapped relative to
      // childKey to avoid collisions with the same nodes in the main tree.
      const moduleKey = getModuleKey(child.moduleSlug, child.moduleVariant);
      if (matchingModules.has(moduleKey)) {
        results.push({
          ...child,
          key: childKey,
          color: child.color,
          children: remapDescendantKeys(child.children, childKey),
        });
      }
    } else if (child.children.length) {
      // Branch node - find which children match
      const matchingChildren = child.children.filter((c) => {
        if (!c.moduleSlug) return false;
        const key = getModuleKey(c.moduleSlug, c.moduleVariant);
        return matchingModules.has(key);
      });
      const matchingChildCount = matchingChildren.length;

      if (
        matchingChildCount > MIN_MATCHING_CHILDREN_TO_GROUP ||
        matchingChildCount === child.children.length
      ) {
        // Business rule: Ungroup branch if children have different DisplayOrder values
        // to allow the final sort to reorder them according to metadata preferences
        const shouldUngroup = shouldUngroupBranch(
          child.children,
          displayOrderMap,
        );

        if (shouldUngroup) {
          // Ungroup: recurse into children to flatten them into the results
          const filteredChildren = filterDescendants(
            child,
            matchingModules,
            displayOrderMap,
            {
              path: childKey,
              parentColor: child.color,
            },
          );
          results.push(...filteredChildren);
        } else {
          // Keep branch grouped (children have same DisplayOrder or no DisplayOrder).
          // Only matching children are kept; non-matching siblings are dropped.
          // Remap all descendant keys relative to childKey to avoid collisions with
          // the same nodes appearing elsewhere in the tree (e.g. under `modules/`).
          // This must be recursive because module nodes (e.g. gym op nodes) may
          // themselves have children (e.g. gym number nodes) that need remapping.
          results.push({
            ...child,
            key: childKey,
            children: remapDescendantKeys(matchingChildren, childKey),
          });
        }
      } else {
        // Recurse into children
        const filteredChildren = filterDescendants(
          child,
          matchingModules,
          displayOrderMap,
          {
            path: childKey,
            parentColor: child.color,
          },
        );
        results.push(...filteredChildren);
      }
    }
  }

  return results;
}

/**
 * Recursively remaps the keys of a subtree so they are relative to a new
 * parent path instead of the original tree path.
 *
 * For nodes with a `tag`, the tag is used as the path segment (stable and
 * human-readable). For nodes without a tag (e.g. gym number nodes which have
 * neither a tag nor a moduleSlug), the last segment of the existing key is
 * used — this preserves the index-based segment assigned by standardizeNode
 * ("1", "2", ...) while anchoring it under the new parent path.
 */
function remapDescendantKeys(
  children: readonly StrictNodeModel[],
  parentKey: string,
): StrictNodeModel[] {
  return children.map((c) => {
    const splitKey = c.key.split('/');
    const segment = c.tag ?? splitKey[splitKey.length - 1] ?? c.key;
    const newKey = `${parentKey}/${segment}`;
    return {
      ...c,
      key: newKey,
      children: remapDescendantKeys(c.children, newKey),
    };
  });
}

/**
 * Normalizes a filtered icon to StrictNodeModel icon format.
 * Converts string icons to object form as required by StrictNodeModel.
 */
function normalizeFilteredIcon(
  child: StrictNodeModel,
): StrictNodeModel['icon'] {
  const filteredIcon = child.filtered?.icon;
  if (!filteredIcon) return child.icon;

  return typeof filteredIcon === 'string'
    ? { image: filteredIcon, text: undefined }
    : filteredIcon;
}

/**
 * Creates a unique key for a module based on its slug and variant.
 * @param moduleSlug - The module slug
 * @param moduleVariant - The module variant (defaults to 0 if not provided)
 * @returns A string key in the format "slug:variant"
 */
function getModuleKey(
  moduleSlug: string,
  moduleVariant: number | undefined,
): string {
  return `${moduleSlug}:${moduleVariant ?? 0}`;
}

/**
 * Determines if a branch node should be ungrouped based on its children's DisplayOrder values.
 *
 * Business requirement: When all children in a branch have different DisplayOrder values,
 * the branch should be ungrouped so that the final sort can reorder the children according
 * to their DisplayOrder metadata. This allows content managers to control the presentation
 * order of modules even when they're grouped under a common parent in the tree structure.
 *
 * @param children - The child nodes to check
 * @param displayOrderMap - Map of module keys to their DisplayOrder values
 * @returns true if the branch should be ungrouped (children have different DisplayOrder values)
 */
function shouldUngroupBranch(
  children: readonly StrictNodeModel[],
  displayOrderMap: Map<string, number>,
): boolean {
  // Extract DisplayOrder values for all children that have modules and DisplayOrder metadata
  const childDisplayOrders = children
    .map((c) => {
      if (!c.moduleSlug) return null;
      const key = getModuleKey(c.moduleSlug, c.moduleVariant);
      return displayOrderMap.get(key) ?? null;
    })
    .filter((order): order is number => order !== null);

  // If we have multiple DisplayOrder values, check if they differ
  if (childDisplayOrders.length <= 1) {
    return false;
  }

  const firstOrder = childDisplayOrders[0];
  return childDisplayOrders.some((order) => order !== firstOrder);
}
